/**
 * nahaFlightService.ts — 那覇空港 FIS（フライト情報）取得・正規化・予約照合
 *
 * データ源: sys/api/naha_flights.php（那覇空港サイトの FIS JSON をプロキシ+60秒キャッシュ）
 *   - fis_national.json（国内線）/ fis_internal.json（国際線）
 *   - air_code.json（航空会社コード表）
 *
 * 照合は2段構え:
 *   1. 決定的正規化（本ファイル）: 全半角統一 + 航空会社名→IATAコード + 数字抽出 + コードシェア展開
 *   2. 揺らぎが解決できないものだけ Gemini（sys/api/flight_fix_gemini.php）で修正案を出す
 */
import { SYS_API_BASE } from '../constants/calendar';

// --- 型定義 ---

export interface NahaArrival {
  carrier: string;        // IATA 2レター（例: NH）
  flightNo: number;       // 471
  originJp: string;       // 東京（全角スペース除去済み）
  originEn: string;
  scheduled: string | null;  // "HH:mm"（当日分）
  estimated: string | null;  // 変更時刻 "HH:mm"
  actual: string | null;     // 実績時刻 "HH:mm"
  statusJp: string;       // 到着済 / 欠航 / 手荷物受取済 等
  statusEn: string;       // ARRIVED / CANCELLED 等
  isInternational: boolean;
  codeshares: { carrier: string; flightNo: number }[];
  dateYmd: string;        // "YYYYMMDD"（定刻の日付）
  /** 遅延理由などの備考。宮古空港のみ取得できる（那覇のFISには無い。2026-09-01） */
  remark?: string | null;
}

export interface FlightMatch {
  arrival: NahaArrival;
  matchedVia: 'direct' | 'codeshare';
}

export interface NahaFlightData {
  fetchedAt: string;
  arrivals: NahaArrival[];
  airlineAliasMap: Record<string, string>; // 名前/英名（大文字化）→ IATAコード
}

// --- 定数 ---

// air_code.json に載っていない・予約でよく使われる略記のエイリアス
export const STATIC_AIRLINE_ALIASES: Record<string, string> = {
  'JAL': 'JL', '日本航空': 'JL', 'JAPANAIRLINES': 'JL',
  'ANA': 'NH', '全日空': 'NH', '全日本空輸': 'NH', 'ALLNIPPONAIRWAYS': 'NH',
  'JTA': 'NU', '日本トランスオーシャン航空': 'NU', '日本トランスオーシャン': 'NU',
  'SNA': '6J', 'ソラシド': '6J', 'ソラシドエア': '6J', 'SOLASEED': '6J', 'SOLASEEDAIR': '6J',
  'SKY': 'BC', 'スカイマーク': 'BC', 'SKYMARK': 'BC',
  'APJ': 'MM', 'ピーチ': 'MM', 'PEACH': 'MM',
  'JJP': 'GK', 'ジェットスター': 'GK', 'JETSTAR': 'GK',
  'SFJ': '7G', 'スターフライヤー': '7G', 'STARFLYER': '7G',
  'RAC': 'RC', '琉球エアーコミューター': 'RC', '琉球エアコミューター': 'RC',
};

// --- FIS行のパース ---

const FIS_IDX = {
  CARRIER: 0, FLIGHT_NO: 1, ORIGIN_JP: 2, ORIGIN_EN: 3,
  SCHEDULED: 4, ESTIMATED: 5, ACTUAL: 6, STATUS_JP: 7, STATUS_EN: 8,
  GATE: 9, DIR: 10, CS_MARK: 11, CS_COUNT: 12, CS_START: 13,
} as const;

function toHm(ts: string | undefined | null): string | null {
  if (!ts || ts.length < 12) return null;
  return `${ts.slice(8, 10)}:${ts.slice(10, 12)}`;
}

function parseFisRow(row: string[], isInternational: boolean): NahaArrival | null {
  if (!Array.isArray(row) || row.length < 11) return null;
  if (row[FIS_IDX.DIR] !== 'A') return null; // 到着便のみ
  const carrier = String(row[FIS_IDX.CARRIER] || '').replace(/^﻿/, '').trim().toUpperCase();
  const flightNo = parseInt(String(row[FIS_IDX.FLIGHT_NO] || ''), 10);
  if (!carrier || isNaN(flightNo)) return null;
  const scheduledRaw = String(row[FIS_IDX.SCHEDULED] || '');

  const codeshares: { carrier: string; flightNo: number }[] = [];
  for (let i = FIS_IDX.CS_START; i + 1 < row.length; i += 2) {
    const csCarrier = String(row[i] || '').trim().toUpperCase();
    const csNo = parseInt(String(row[i + 1] || ''), 10);
    if (csCarrier && !isNaN(csNo)) codeshares.push({ carrier: csCarrier, flightNo: csNo });
  }

  return {
    carrier,
    flightNo,
    originJp: String(row[FIS_IDX.ORIGIN_JP] || '').replace(/[\s　]/g, ''),
    originEn: String(row[FIS_IDX.ORIGIN_EN] || ''),
    scheduled: toHm(scheduledRaw),
    estimated: toHm(String(row[FIS_IDX.ESTIMATED] || '')),
    actual: toHm(String(row[FIS_IDX.ACTUAL] || '')),
    statusJp: String(row[FIS_IDX.STATUS_JP] || ''),
    statusEn: String(row[FIS_IDX.STATUS_EN] || ''),
    isInternational,
    codeshares,
    dateYmd: scheduledRaw.slice(0, 8),
  };
}

// --- 国際線（fis_internal.json）のパース ---
// 国際線は data が {FlightList:[{Flight:[{Item:[...], sort}]}]} の別形式。
// 便名・航空会社・出発地は top-level Item 内の Master(ネストItem)に、時刻は top-level Item にある。

/** 多言語フィールドから日本語値を取り出す（{value} or {Language:[{code:'JA',value}]}） */
function langValueJa(item: any): string | null {
  if (!item) return null;
  if (item.value != null) return String(item.value);
  if (Array.isArray(item.Language)) {
    const ja = item.Language.find((l: any) => l.code === 'JA') || item.Language[0];
    return ja?.value != null ? String(ja.value) : null;
  }
  return null;
}

/** ネストした Body[JA].Text[].value からステータス文言を再帰抽出 */
function extractStatusText(node: any): string | null {
  if (!node || typeof node !== 'object') return null;
  if (Array.isArray(node.Body)) {
    const ja = node.Body.find((b: any) => b.code === 'JA') || node.Body[0];
    if (ja && Array.isArray(ja.Text) && ja.Text[0]?.value) return String(ja.Text[0].value);
  }
  for (const v of Object.values(node)) {
    if (v && typeof v === 'object') { const r = extractStatusText(v); if (r) return r; }
  }
  return null;
}

/** 国際線の1便（Flightオブジェクト）を NahaArrival に変換 */
function parseIntlFlight(flight: any): NahaArrival | null {
  const items: any[] = flight?.Item || [];
  const top: Record<string, any> = {};
  const masters: any[] = [];
  const slaves: any[] = [];
  for (const it of items) {
    if (it.id === 'Master' && Array.isArray(it.Item)) masters.push(it);
    else if (it.id === 'Slave' && Array.isArray(it.Item)) slaves.push(it);
    else if (!(it.id in top)) top[it.id] = it.value;
  }
  if (top.DA !== 'A') return null;
  const master = masters[0];
  if (!master) return null;
  const mMap: Record<string, any> = {};
  for (const x of master.Item) if (!(x.id in mMap)) mMap[x.id] = x;

  const flightCode = (langValueJa(mMap.DisplayFlight) || langValueJa(mMap.Flight) || '').toUpperCase();
  const carrier = (langValueJa(mMap.AirlineCd) || '').toUpperCase()
    || flightCode.replace(/[0-9].*$/, '');
  const numStr = langValueJa(mMap.FltNumNo) || flightCode.replace(/^[A-Z0-9]{2,3}/, '');
  const flightNo = parseInt(String(numStr).replace(/\D/g, ''), 10);
  if (!carrier || isNaN(flightNo)) return null;

  const padHm = (t: string | null) => {
    if (!t) return null;
    const m = String(t).match(/^(\d{1,2}):(\d{2})$/);
    return m ? `${m[1].padStart(2, '0')}:${m[2]}` : t;
  };

  const codeshares: { carrier: string; flightNo: number }[] = [];
  for (const s of slaves) {
    const sm: Record<string, any> = {};
    for (const x of s.Item) if (!(x.id in sm)) sm[x.id] = x;
    const code = (langValueJa(sm.DisplayFlight) || langValueJa(sm.Flight) || '').toUpperCase();
    const cc = (langValueJa(sm.AirlineCd) || code.replace(/[0-9].*$/, '')).toUpperCase();
    const cn = parseInt(String(langValueJa(sm.FltNumNo) || code.replace(/^[A-Z0-9]{2,3}/, '')).replace(/\D/g, ''), 10);
    if (cc && !isNaN(cn)) codeshares.push({ carrier: cc, flightNo: cn });
  }

  const statusJp = extractStatusText(master) || '';

  return {
    carrier,
    flightNo,
    originJp: (langValueJa(mMap.AreaName) || '').replace(/[\s　]/g, ''),
    originEn: '',
    scheduled: padHm(top.ST),
    estimated: padHm(top.ET),
    actual: padHm(top.AT),
    statusJp,
    statusEn: '',
    isInternational: true,
    codeshares,
    dateYmd: String(top.SYMD || ''),
  };
}

// --- 取得 ---

export async function fetchNahaFlights(targetYmd: string): Promise<NahaFlightData> {
  const res = await fetch(`${SYS_API_BASE}/naha_flights.php`);
  if (!res.ok) throw new Error(`フライト情報の取得に失敗しました (HTTP ${res.status})`);
  const json = await res.json();

  const arrivals: NahaArrival[] = [];
  // 国内線: data は string[][] の平坦配列
  const natData = json.national?.data;
  if (Array.isArray(natData)) {
    natData.forEach((row: string[]) => {
      const a = parseFisRow(row, false);
      if (a && a.dateYmd === targetYmd) arrivals.push(a);
    });
  }
  // 国際線: data は {FlightList:[{Flight:[...]}]} のネスト形式。Master に便名/会社/出発地
  const intlFlights = json.internal?.data?.FlightList?.[0]?.Flight;
  if (Array.isArray(intlFlights)) {
    intlFlights.forEach((f: any) => {
      const a = parseIntlFlight(f);
      if (a && a.dateYmd === targetYmd) arrivals.push(a);
    });
  }

  const normKey = (v: string) => String(v).normalize('NFKC').replace(/[\s　]/g, '').toUpperCase();
  const airlineAliasMap: Record<string, string> = {};
  Object.entries(STATIC_AIRLINE_ALIASES).forEach(([k, v]) => { airlineAliasMap[normKey(k)] = v; });
  (json.air_code || []).forEach((ac: any) => {
    const code = String(ac.carrier_fs_code || '').toUpperCase();
    if (!code) return;
    if (ac.name) airlineAliasMap[normKey(ac.name)] = code;
    if (ac.english_name) airlineAliasMap[normKey(ac.english_name)] = code;
  });

  return { fetchedAt: json.fetched_at || '', arrivals, airlineAliasMap };
}

// --- 予約側フライト表記の正規化 ---

/**
 * 正規化: NFKC で全角英数字→半角・半角カナ→全角カナ（ｿﾗｼﾄﾞ→ソラシド）を一括処理し、
 * 大文字化 + 時刻(14:30)・「便/着/発」等のノイズを空白化する。空白は残す（トークン分割用）。
 */
function normalizeText(raw: string): string {
  return String(raw)
    .normalize('NFKC')
    .toUpperCase()
    .replace(/\d{1,2}\s*[:：]\s*\d{2}/g, ' ')      // 時刻 14:30 を便番号と誤認しない
    .replace(/便|FLIGHT|FLT\.?|NO\.?|着|発|行|ゆき|ﾋﾞﾝ/g, ' ');
}

/**
 * 予約の到着便表記から {carrier, flightNo} を推定する。便番号が末尾でなくても抽出する。
 * 例: "JAL903" → {JL,903} / "NH 0471便" → {NH,471} / "ソラシド61" → {6J,61}
 *     "羽田 JAL903 14:30着" → {JL,903} / "ｿﾗｼﾄﾞ 51" → {6J,51}
 * 解決できなければ null。
 */
export function parseReservationFlight(
  raw: string,
  aliasMap: Record<string, string>,
): { carrier: string; flightNo: number } | null {
  const compact = normalizeText(raw).replace(/[\s　]/g, '');
  if (!compact) return null;

  // 1) 航空会社名/エイリアス（JAL・ANA・ソラシド・全日空 等）を長いトークン優先で探し、
  //    そのトークンの直後（優先）または直前の数字を便番号にする。
  const tokens = Object.keys(aliasMap)
    .filter(t => t.length >= 2)
    .sort((a, b) => b.length - a.length);
  for (const tok of tokens) {
    const idx = compact.indexOf(tok);
    if (idx === -1) continue;
    const after = compact.slice(idx + tok.length).match(/^\D*?0*(\d{1,4})/);
    if (after) return { carrier: aliasMap[tok], flightNo: parseInt(after[1], 10) };
    const before = compact.slice(0, idx).match(/0*(\d{1,4})\D*$/);
    if (before) return { carrier: aliasMap[tok], flightNo: parseInt(before[1], 10) };
  }

  // 2) 素の IATA 2レターコード + 便番号（NH469 / 6J61 / 7G13 等）
  const m = compact.match(/([A-Z]{2}|[0-9][A-Z]|[A-Z][0-9])0*(\d{1,4})/);
  if (m) return { carrier: m[1], flightNo: parseInt(m[2], 10) };

  return null;
}

/** 到着便リストから (carrier, flightNo) に合う便を探す（コードシェア含む） */
export function findArrival(
  arrivals: NahaArrival[],
  carrier: string,
  flightNo: number,
): FlightMatch | null {
  const direct = arrivals.find(a => a.carrier === carrier && a.flightNo === flightNo);
  if (direct) return { arrival: direct, matchedVia: 'direct' };
  const cs = arrivals.find(a => a.codeshares.some(c => c.carrier === carrier && c.flightNo === flightNo));
  if (cs) return { arrival: cs, matchedVia: 'codeshare' };
  return null;
}

// --- Gemini 修正案 ---

export interface FlightFixSuggestion {
  id: string;
  suggestion: string | null;
  carrier?: string;
  flight_no?: number;
  confidence?: number;
  reason?: string;
}

export async function fetchGeminiFlightFixes(
  items: { id: string; text: string }[],
  arrivals: NahaArrival[],
): Promise<FlightFixSuggestion[]> {
  const candidates = arrivals.map(a => ({
    carrier: a.carrier,
    flight_no: a.flightNo,
    origin: a.originJp,
    scheduled: a.scheduled,
    estimated: a.estimated || a.actual || null,
    status: a.statusJp || null,
    codeshares: a.codeshares.map(c => `${c.carrier}${c.flightNo}`).join(','),
  }));
  const res = await fetch(`${SYS_API_BASE}/flight_fix_gemini.php`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items, candidates }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new Error(err?.error || `Gemini修正案の取得に失敗しました (HTTP ${res.status})`);
  }
  const json = await res.json();
  return json.suggestions || [];
}

// --- 表示ヘルパー ---

/** ステータスに応じた表示色（欠航=赤 / 到着系=緑 / 遅延等その他=琥珀） */
export function flightStatusColor(a: NahaArrival): { bg: string; text: string } {
  const en = a.statusEn.toUpperCase();
  if (en.includes('CANCEL') || a.statusJp.includes('欠航')) return { bg: '#fee2e2', text: '#991b1b' };
  // 「到着予定」はまだ着いていない。緑（到着済み）と混同しないよう先に弾く（2026-09-01）
  if (a.statusJp.includes('予定')) return { bg: '#dbeafe', text: '#1e40af' };
  if (en.includes('ARRIV') || a.statusJp.includes('到着') || a.statusJp.includes('手荷物')) return { bg: '#dcfce7', text: '#166534' };
  if (a.statusJp.includes('遅')) return { bg: '#fef3c7', text: '#92400e' };
  return { bg: '#dbeafe', text: '#1e40af' };
}
